import {
  UI_CELL_SIZE,
  UI_ICON_SIZE,
  drawPixelSpriteToCanvas
} from '../rendering/PixelGrid.js'
import { CHEST_SPRITE } from '../rendering/Sprites.js'
import {
  HUD_EMPTY_HEART_SPRITE,
  HUD_ARROW_SPRITE,
  HUD_COIN_SPRITE,
  HUD_HALF_HEART_SPRITE,
  HUD_HEART_SPRITE,
  HUD_DARK_KING_KEY_SPRITE,
  HUD_QUARTER_HEART_SPRITE,
  HUD_SHOP_BAG_SPRITE,
  HUD_THREE_QUARTER_HEART_SPRITE,
  HUD_KEY_SPRITE,
  HUD_MOON_SPRITE,
  HUD_SHIELD_SPRITE,
  HUD_SUN_SPRITE,
  HUD_SWORD_SPRITE
} from '../rendering/UISprites.js'
import {
  HEART_STATE,
  describeHeartValue,
  getHeartSlotState,
  normalizeHeartValue
} from '../player/HeartHealth.js'
import { Minimap } from './Minimap.js'

export class HUD {
  constructor(game) {
    this.game = game
    this.heartsDisplay = document.getElementById('hearts-display')
    this.keysDisplay = document.getElementById('keys-display')
    this.coinsDisplay = document.getElementById('coins-display')
    this.zoneName = document.getElementById('zone-name')
    this.timeDisplay = document.getElementById('time-display')
    this.arrowStatus = document.getElementById('arrow-status')
    this.arrowCount = document.getElementById('arrow-count')
    this.arrowCooldownFill = document.getElementById('arrow-cooldown-fill')
    this.shieldStatus = document.getElementById('shield-status')
    this.shieldCooldownFill = document.getElementById('shield-cooldown-fill')
    this.minimap = new Minimap(game, document.getElementById('minimap'))
    this.buttonSlots = {
      top: document.getElementById('btn-top'),
      left: document.getElementById('btn-left'),
      right: document.getElementById('btn-right'),
      bottom: document.getElementById('btn-bottom')
    }
    this.lastHearts = -1
    this.lastMaxHearts = -1
    this.lastKeys = -1
    this.lastDarkKingKeys = -1
    this.lastCoins = -1
    this.lastHasSword = null
    this.lastHasBow = null
    this.lastShieldId = undefined
    this.lastNearbyChestId = null
    this.lastPhase = null
    this.lastIsNight = null

    this.setupButtonHud()
    this.setupEquipmentStatus()
  }

  update() {
    if (!this.game.player) return

    const player = this.game.player

    // Hearts
    if (player.hearts !== this.lastHearts || player.maxHearts !== this.lastMaxHearts) {
      this.lastHearts = player.hearts
      this.lastMaxHearts = player.maxHearts
      this.renderHearts(player.hearts, player.maxHearts)
    }

    // Keys
    if (player.keys !== this.lastKeys || player.darkKingKeys !== this.lastDarkKingKeys) {
      this.lastKeys = player.keys
      this.lastDarkKingKeys = player.darkKingKeys
      this.renderKeys(player.keys, player.darkKingKeys)
    }

    if (player.coins !== this.lastCoins) {
      this.lastCoins = player.coins
      this.renderCoins(player.coins)
    }

    // Zone name
    const zone = this.game.world?.isPlayerInPostgameVillage?.()
      ? { displayName: 'Village' }
      : this.game.getCurrentZone()
    if (zone) {
      this.zoneName.textContent = zone.displayName
    }
    this.minimap.update()
    this.updateEquipmentStatus(player)

    // Time of day
    const phase = this.game.dayNight.getPhase()
    const isNight = this.game.dayNight.isNight()
    if (phase !== this.lastPhase || isNight !== this.lastIsNight) {
      this.lastPhase = phase
      this.lastIsNight = isNight
      this.renderTime(phase, isNight)
    }

    const nearbyChestId = this.game.itemManager?.getNearestClosedChest()?.id || null
    if (
      player.hasSword !== this.lastHasSword ||
      player.hasBow !== this.lastHasBow ||
      player.shieldId !== this.lastShieldId ||
      nearbyChestId !== this.lastNearbyChestId
    ) {
      this.lastHasSword = player.hasSword
      this.lastHasBow = player.hasBow
      this.lastShieldId = player.shieldId
      this.lastNearbyChestId = nearbyChestId
      this.updateButtonHud()
    }
  }

  setupEquipmentStatus() {
    const arrowIcon = document.getElementById('arrow-status-icon')
    const shieldIcon = document.getElementById('shield-status-icon')
    if (arrowIcon) drawPixelSpriteToCanvas(arrowIcon, HUD_ARROW_SPRITE, null, 2)
    if (shieldIcon) drawPixelSpriteToCanvas(shieldIcon, HUD_SHIELD_SPRITE, null, 2)
  }

  updateEquipmentStatus(player) {
    this.arrowStatus.hidden = false
    this.arrowStatus.classList.toggle('is-unavailable', !player.hasBow)
    this.arrowStatus.setAttribute(
      'aria-label',
      player.hasBow ? 'Bow and arrows' : 'Bow and arrows locked'
    )
    const totalArrows = Object.values(player.arrows).reduce((sum, count) => sum + count, 0)
    this.arrowCount.textContent = `×${totalArrows}`
    if (!player.hasBow) {
      this.arrowCooldownFill.style.width = '0%'
    } else {
      const arrowReady = player.arrowCooldown > 0
        ? 1 - Math.min(1, player.arrowCooldown / player.arrowCooldownDuration)
        : 1 - Math.min(1, player.arrowBurstCount / player.arrowBurstSize)
      this.arrowCooldownFill.style.width = `${Math.round(arrowReady * 100)}%`
    }

    this.shieldStatus.classList.toggle('is-unavailable', player.shieldId === null)
    const shieldReady = player.shieldCooldown > 0
      ? 1 - Math.min(1, player.shieldCooldown / player.shieldCooldownDuration)
      : player.isShielding
        ? 1 - Math.min(1, player.shieldHoldTime / player.shieldHoldDuration)
        : 1
    this.shieldCooldownFill.style.width = `${Math.round(shieldReady * 100)}%`
  }

  renderHearts(current, max, container = this.heartsDisplay) {
    const safeCurrent = normalizeHeartValue(current, max)
    const slotCount = Math.ceil(max)

    container.innerHTML = ''
    container.setAttribute('role', 'meter')
    container.setAttribute('aria-label', 'Health')
    container.setAttribute('aria-valuemin', '0')
    container.setAttribute('aria-valuemax', String(max))
    container.setAttribute('aria-valuenow', String(safeCurrent))
    container.setAttribute(
      'aria-valuetext',
      `${describeHeartValue(safeCurrent)} out of ${describeHeartValue(max)}`
    )

    for (let i = 0; i < slotCount; i++) {
      const state = getHeartSlotState(safeCurrent, i)
      const sprite = state === HEART_STATE.FULL
        ? HUD_HEART_SPRITE
        : state === HEART_STATE.THREE_QUARTERS
          ? HUD_THREE_QUARTER_HEART_SPRITE
        : state === HEART_STATE.HALF
          ? HUD_HALF_HEART_SPRITE
          : state === HEART_STATE.QUARTER
            ? HUD_QUARTER_HEART_SPRITE
          : HUD_EMPTY_HEART_SPRITE
      const heart = this.createIconCanvas()
      heart.setAttribute('aria-hidden', 'true')
      drawPixelSpriteToCanvas(heart, sprite)
      container.appendChild(heart)
    }
  }

  renderKeys(count, darkKingCount = 0) {
    this.keysDisplay.innerHTML = ''
    if (count <= 0 && darkKingCount <= 0) return

    const appendKey = (sprite, labelText, accessibleName) => {
      const icon = this.createIconCanvas(accessibleName)
      drawPixelSpriteToCanvas(icon, sprite)

      const label = document.createElement('span')
      label.className = 'key-count'
      label.textContent = `×${labelText}`

      this.keysDisplay.appendChild(icon)
      this.keysDisplay.appendChild(label)
    }

    if (count > 0) appendKey(HUD_KEY_SPRITE, count, 'Normal key')
    if (darkKingCount > 0) {
      appendKey(HUD_DARK_KING_KEY_SPRITE, darkKingCount, "Dark King's key")
    }
  }

  renderCoins(count) {
    this.coinsDisplay.innerHTML = ''
    const icon = this.createIconCanvas('Gold coin')
    drawPixelSpriteToCanvas(icon, HUD_COIN_SPRITE)
    const label = document.createElement('span')
    label.className = 'coin-count'
    label.textContent = `×${count}`
    this.coinsDisplay.append(icon, label)
  }

  renderTime(phase, isNight) {
    this.timeDisplay.innerHTML = ''

    const icon = this.createIconCanvas(isNight ? 'Moon' : 'Sun')
    drawPixelSpriteToCanvas(icon, isNight ? HUD_MOON_SPRITE : HUD_SUN_SPRITE)

    const label = document.createElement('span')
    label.textContent = phase.toUpperCase()

    this.timeDisplay.appendChild(icon)
    this.timeDisplay.appendChild(label)
  }

  createIconCanvas(label) {
    const canvas = document.createElement('canvas')
    canvas.width = UI_ICON_SIZE
    canvas.height = UI_ICON_SIZE
    canvas.className = 'pixel-icon'
    if (label) canvas.setAttribute('aria-label', label)
    return canvas
  }

  setupButtonHud() {
    for (const slot of Object.values(this.buttonSlots)) {
      if (!slot) continue
      slot.innerHTML = ''
      const canvas = document.createElement('canvas')
      canvas.width = UI_CELL_SIZE
      canvas.height = UI_CELL_SIZE
      canvas.className = 'button-icon'
      canvas.setAttribute('aria-hidden', 'true')
      slot.appendChild(canvas)
    }
    this.updateButtonHud()
  }

  updateButtonHud() {
    this.clearButtonSlot('top')
    this.clearButtonSlot('left')
    this.clearButtonSlot('right')

    if (this.game.player?.hasBow) this.drawSpriteInSlot('top', HUD_ARROW_SPRITE, 2)
    if (this.game.player?.shieldId) this.drawSpriteInSlot('left', HUD_SHIELD_SPRITE, 2)
    const backSlot = this.buttonSlots.right
    const backCanvas = backSlot?.querySelector('canvas')
    if (backSlot && backCanvas) {
      backSlot.classList.remove('is-empty')
      backSlot.disabled = false
      const ctx = backCanvas.getContext('2d')
      ctx.clearRect(0, 0, backCanvas.width, backCanvas.height)
      ctx.fillStyle = '#f3d37a'
      ctx.font = 'bold 8px monospace'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('B', 4, 4.5)
    }

    const nearbyChest = this.game.itemManager?.getNearestClosedChest()
    if (nearbyChest) {
      const isShop = nearbyChest.kind === 'shop'
      this.buttonSlots.bottom?.classList.toggle('shop-action', isShop)
      this.buttonSlots.bottom?.setAttribute(
        'aria-label',
        isShop ? 'Enter shop' : 'Open chest'
      )
      this.drawSpriteInSlot(
        'bottom',
        isShop ? HUD_SHOP_BAG_SPRITE : CHEST_SPRITE,
        1
      )
    } else if (this.game.player?.hasSword) {
      this.buttonSlots.bottom?.classList.remove('shop-action')
      this.buttonSlots.bottom?.setAttribute('aria-label', 'Attack')
      this.drawSpriteInSlot('bottom', HUD_SWORD_SPRITE, 2)
    } else {
      this.buttonSlots.bottom?.classList.remove('shop-action')
      this.buttonSlots.bottom?.setAttribute('aria-label', 'Action')
      this.clearButtonSlot('bottom')
    }
  }

  clearButtonSlot(name) {
    const slot = this.buttonSlots[name]
    const canvas = slot?.querySelector('canvas')
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    slot.classList.add('is-empty')
    if ('disabled' in slot) slot.disabled = true
  }

  drawSpriteInSlot(name, sprite, pixelScale = 1) {
    const slot = this.buttonSlots[name]
    const canvas = slot?.querySelector('canvas')
    if (!canvas || !sprite) return
    slot.classList.remove('is-empty')
    if ('disabled' in slot) slot.disabled = false
    drawPixelSpriteToCanvas(canvas, sprite, null, pixelScale)
  }
}
